import type { Condition, ConditionGroup } from './primitives.js';

/**
 * INTERPRETADOR DE CONDICOES E FORMULAS
 *
 * Usado pelo formulario (visibilidade condicional), pelos filtros e pelos
 * campos calculados. Nao existe `eval` nem `new Function` aqui: a formula e
 * tokenizada e avaliada por um parser de precedencia proprio, restrito a
 * numeros, identificadores e os quatro operadores aritmeticos. Um schema
 * vindo do painel e dado nao confiavel — tratamos como tal.
 */

/** Le `a.b.c` num objeto, tolerando ausencia em qualquer nivel. */
export function readPath(source: unknown, path: string): unknown {
  if (source == null) return undefined;
  let cursor: unknown = source;
  for (const segment of path.split('.')) {
    if (cursor == null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function toComparable(value: unknown): number | string | null {
  if (typeof value === 'number' || typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) return value.getTime();
  return null;
}

export function evaluateCondition(condition: Condition, context: unknown): boolean {
  const actual = readPath(context, condition.path);
  const expected = condition.value;

  switch (condition.op) {
    case 'eq':
      return Array.isArray(actual) ? actual.includes(expected) : actual === expected;
    case 'neq':
      return Array.isArray(actual) ? !actual.includes(expected) : actual !== expected;
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const left = toComparable(actual);
      const right = toComparable(expected);
      if (left === null || right === null || typeof left !== typeof right) return false;
      if (condition.op === 'gt') return left > right;
      if (condition.op === 'gte') return left >= right;
      if (condition.op === 'lt') return left < right;
      return left <= right;
    }
    case 'in':
      return Array.isArray(expected) && expected.includes(actual as never);
    case 'not_in':
      return Array.isArray(expected) && !expected.includes(actual as never);
    case 'contains':
      if (Array.isArray(actual)) return actual.includes(expected as never);
      if (typeof actual === 'string' && typeof expected === 'string') {
        return actual.toLowerCase().includes(expected.toLowerCase());
      }
      return false;
    case 'is_empty':
      return isEmpty(actual);
    case 'is_not_empty':
      return !isEmpty(actual);
    default:
      return false;
  }
}

/** Grupo vazio e verdadeiro: "sem restricao" nunca deve esconder nada. */
export function evaluateConditionGroup(group: ConditionGroup | null | undefined, context: unknown): boolean {
  if (!group || group.rules.length === 0) return true;
  const results = group.rules.map((rule) => evaluateCondition(rule, context));
  return group.mode === 'any' ? results.some(Boolean) : results.every(Boolean);
}

// --- formulas de campos calculados ---

type Token = { kind: 'number'; value: number } | { kind: 'ident'; value: string } | { kind: 'op'; value: string };

const OPERATOR_PRECEDENCE: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2 };

function tokenize(formula: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < formula.length) {
    const char = formula[index]!;

    if (/\s/.test(char)) {
      index += 1;
      continue;
    }
    if (/[0-9.]/.test(char)) {
      let literal = '';
      while (index < formula.length && /[0-9.]/.test(formula[index]!)) literal += formula[index++];
      const value = Number(literal);
      if (Number.isNaN(value)) throw new Error(`Numero invalido na formula: "${literal}"`);
      tokens.push({ kind: 'number', value });
      continue;
    }
    if (/[a-zA-Z_]/.test(char)) {
      let identifier = '';
      while (index < formula.length && /[a-zA-Z0-9_.]/.test(formula[index]!)) identifier += formula[index++];
      tokens.push({ kind: 'ident', value: identifier });
      continue;
    }
    if ('+-*/()'.includes(char)) {
      tokens.push({ kind: 'op', value: char });
      index += 1;
      continue;
    }
    throw new Error(`Caractere nao permitido em formula: "${char}"`);
  }

  return tokens;
}

/**
 * Avalia a formula com precedencia correta (Shunting-yard -> RPN).
 * Retorna `null` quando algum identificador esta ausente ou nao e numerico,
 * de modo que o campo calculado simplesmente nao aparece em vez de exibir NaN.
 */
export function evaluateFormula(formula: string, context: Record<string, unknown>): number | null {
  let tokens: Token[];
  try {
    tokens = tokenize(formula);
  } catch {
    return null;
  }

  const output: Token[] = [];
  const operators: string[] = [];

  for (const token of tokens) {
    if (token.kind === 'number' || token.kind === 'ident') {
      output.push(token);
      continue;
    }
    if (token.value === '(') {
      operators.push(token.value);
      continue;
    }
    if (token.value === ')') {
      while (operators.length > 0 && operators[operators.length - 1] !== '(') {
        output.push({ kind: 'op', value: operators.pop()! });
      }
      if (operators.pop() !== '(') return null; // parenteses desbalanceado
      continue;
    }
    const precedence = OPERATOR_PRECEDENCE[token.value];
    if (precedence === undefined) return null;
    while (operators.length > 0) {
      const top = operators[operators.length - 1]!;
      const topPrecedence = OPERATOR_PRECEDENCE[top];
      if (topPrecedence === undefined || topPrecedence < precedence) break;
      output.push({ kind: 'op', value: operators.pop()! });
    }
    operators.push(token.value);
  }

  while (operators.length > 0) {
    const operator = operators.pop()!;
    if (operator === '(') return null;
    output.push({ kind: 'op', value: operator });
  }

  const stack: number[] = [];
  for (const token of output) {
    if (token.kind === 'number') {
      stack.push(token.value);
      continue;
    }
    if (token.kind === 'ident') {
      const raw = readPath(context, token.value);
      const numeric = typeof raw === 'string' ? Number(raw) : raw;
      if (typeof numeric !== 'number' || !Number.isFinite(numeric)) return null;
      stack.push(numeric);
      continue;
    }
    const right = stack.pop();
    const left = stack.pop();
    if (left === undefined || right === undefined) return null;
    switch (token.value) {
      case '+':
        stack.push(left + right);
        break;
      case '-':
        stack.push(left - right);
        break;
      case '*':
        stack.push(left * right);
        break;
      case '/':
        // Divisao por zero e o caso real aqui: preco / area com area vazia.
        if (right === 0) return null;
        stack.push(left / right);
        break;
      default:
        return null;
    }
  }

  const result = stack.pop();
  if (result === undefined || stack.length > 0 || !Number.isFinite(result)) return null;
  return result;
}
